import React, { useState, useEffect } from 'react';
import { ArrowRight, Users, Target, Rocket, CheckCircle2, Building2, Sparkles, Trophy, Award, Medal, UserPlus, X, Mail, User, Briefcase, Play } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  vcPartners, 
  rotatingBuyerTitles 
} from '../mock';
import { getTestimonials, getFeaturedTestimonial, getStartups, submitBuyerApplication, submitVCPartnerApplication, getDecisionMakers } from '../services/api';

// Achievement badge component
const AchievementBadge = ({ achievement }) => {
  if (!achievement) return null;
  
  const badges = {
    winner: {
      label: '🏆 Winner',
      className: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50'
    },
    top3: {
      label: '🥈 Top 3',
      className: 'bg-slate-400/20 text-slate-300 border-slate-400/50'
    },
    top10: {
      label: '🎯 Top 10 Finalist',
      className: 'bg-blue-500/20 text-blue-400 border-blue-500/50'
    }
  };
  
  const badge = badges[achievement];
  if (!badge) return null;
  
  return (
    <Badge className={`${badge.className} text-xs font-semibold`}>
      {badge.label}
    </Badge>
  );
};

// Avatar component with fallback for broken images
const Avatar = ({ src, name, className = "w-12 h-12" }) => {
  const [status, setStatus] = useState('loading'); // 'loading' | 'loaded' | 'error'
  
  // Check if URL points to dead servers
  const isDeadServer = src && (
    src.includes('pitch-buyers.emergent.host') ||
    src.includes('venturestage-1') ||
    src.includes('pitchmates.preview')
  );
  
  useEffect(() => {
    if (!src || isDeadServer) {
      setStatus('error');
      return;
    }
    setStatus('loading');
    
    const img = new Image();
    img.onload = () => setStatus('loaded');
    img.onerror = () => setStatus('error');
    img.src = src;
    
    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [src, isDeadServer]);
  
  // Show fallback for error or no source
  if (status === 'error' || !src || isDeadServer) {
    return (
      <div className={`${className} rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold`}>
        {name?.charAt(0)?.toUpperCase() || '?'}
      </div>
    );
  }
  
  // Show loading state
  if (status === 'loading') {
    return (
      <div className={`${className} rounded-full bg-slate-700 animate-pulse`} />
    );
  }
  
  // Show loaded image
  return (
    <img 
      src={src}
      alt=""
      className={`${className} rounded-full bg-slate-700 object-cover`}
    />
  );
};

const Home = () => {
  const [currentTitleIndex, setCurrentTitleIndex] = useState(0);
  const [testimonials, setTestimonials] = useState([]);
  const [featuredTestimonial, setFeaturedTestimonial] = useState(null);
  const [startups, setStartups] = useState([]);
  const [decisionMakers, setDecisionMakers] = useState([]);
  const [isScrolling, setIsScrolling] = useState(false);
  const scrollTimeoutRef = React.useRef(null);
  
  // Video Modal State
  const [showVideoModal, setShowVideoModal] = useState(false);
  const previewVideoUrl = "https://drive.google.com/file/d/1Wa2I-J5Q1fN-hjgEZZMP5tE_rbBsMubx/view";
  
  // Helper to convert Google Drive URL to embed URL
  const getEmbedUrl = (url) => {
    if (!url) return null;
    if (url.includes('drive.google.com')) {
      const match = url.match(/\/d\/([^\/]+)/);
      if (match) {
        return `https://drive.google.com/file/d/${match[1]}/preview`;
      }
    }
    return url;
  };
  
  // Buyer Join Form State
  const [showBuyerForm, setShowBuyerForm] = useState(false);
  const [buyerFormStep, setBuyerFormStep] = useState(1); // 1 = email, 2 = details
  const [buyerFormData, setBuyerFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    buyer_type: ''
  });
  const [buyerFormLoading, setBuyerFormLoading] = useState(false);
  const [buyerFormSuccess, setBuyerFormSuccess] = useState(false);
  const [buyerFormError, setBuyerFormError] = useState('');

  const buyerTypes = [
    'Cybersecurity',
    'Developer Tools', 
    'Finance Tech',
    'HR Tech',
    'Other'
  ];

  // VC Partner Join Form State
  const [showVCForm, setShowVCForm] = useState(false);
  const [vcFormStep, setVCFormStep] = useState(1); // 1 = email, 2 = details
  const [vcFormData, setVCFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    firm_name: '',
    role: ''
  });
  const [vcFormLoading, setVCFormLoading] = useState(false);
  const [vcFormSuccess, setVCFormSuccess] = useState(false);
  const [vcFormError, setVCFormError] = useState('');

  const vcRoles = [
    'Platform',
    'Investor',
    'Other'
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTitleIndex((prev) => (prev + 1) % rotatingBuyerTitles.length);
    }, 2500);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    loadTestimonials();
    loadFeaturedTestimonial();
    loadStartups();
    loadDecisionMakers();
  }, []);

  const loadDecisionMakers = async () => {
    try {
      const data = await getDecisionMakers();
      setDecisionMakers(data);
    } catch (error) {
      console.error('Error loading decision makers:', error);
    }
  };

  const loadStartups = async () => {
    try {
      const data = await getStartups();
      // Map API response to expected format
      setStartups(data.map(s => ({
        id: s._id,
        name: s.name,
        solution: s.solution,
        category: s.category,
        founder: s.founder,
        stage: s.stage,
        referredBy: s.referred_by,
        achievement: s.achievement
      })));
    } catch (error) {
      console.error('Error loading startups:', error);
    }
  };

  const loadTestimonials = async () => {
    try {
      const data = await getTestimonials();
      setTestimonials(data);
    } catch (error) {
      console.error('Error loading testimonials:', error);
    }
  };

  const loadFeaturedTestimonial = async () => {
    try {
      const data = await getFeaturedTestimonial();
      setFeaturedTestimonial(data);
    } catch (error) {
      console.error('Error loading featured testimonial:', error);
    }
  };

  const handleScroll = () => {
    setIsScrolling(true);
    
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
    
    scrollTimeoutRef.current = setTimeout(() => {
      setIsScrolling(false);
    }, 1000);
  };

  const scrollToApplication = () => {
    document.getElementById('apply-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  // Buyer Form Handlers
  const handleBuyerEmailSubmit = (e) => {
    e.preventDefault();
    if (!buyerFormData.email || !buyerFormData.email.includes('@')) {
      setBuyerFormError('Please enter a valid work email');
      return;
    }
    setBuyerFormError('');
    setBuyerFormStep(2);
  };

  const handleBuyerFormSubmit = async (e) => {
    e.preventDefault();
    if (!buyerFormData.first_name || !buyerFormData.last_name || !buyerFormData.buyer_type) {
      setBuyerFormError('Please fill in all fields');
      return;
    }
    
    setBuyerFormLoading(true);
    setBuyerFormError('');
    
    try {
      await submitBuyerApplication(buyerFormData);
      setBuyerFormSuccess(true);
    } catch (error) {
      setBuyerFormError('Something went wrong. Please try again.');
    } finally {
      setBuyerFormLoading(false);
    }
  };

  const resetBuyerForm = () => {
    setShowBuyerForm(false);
    setBuyerFormStep(1);
    setBuyerFormData({ email: '', first_name: '', last_name: '', buyer_type: '' });
    setBuyerFormSuccess(false);
    setBuyerFormError('');
  };

  // VC Partner Form Handlers
  const handleVCEmailSubmit = (e) => {
    e.preventDefault();
    if (!vcFormData.email || !vcFormData.email.includes('@')) {
      setVCFormError('Please enter a valid work email');
      return;
    }
    setVCFormError('');
    setVCFormStep(2);
  };

  const handleVCFormSubmit = async (e) => {
    e.preventDefault();
    if (!vcFormData.first_name || !vcFormData.last_name || !vcFormData.firm_name || !vcFormData.role) {
      setVCFormError('Please fill in all fields');
      return;
    }
    
    setVCFormLoading(true);
    setVCFormError('');
    
    try {
      await submitVCPartnerApplication(vcFormData);
      setVCFormSuccess(true);
    } catch (error) {
      setVCFormError('Something went wrong. Please try again.');
    } finally {
      setVCFormLoading(false);
    }
  };

  const resetVCForm = () => {
    setShowVCForm(false);
    setVCFormStep(1);
    setVCFormData({ email: '', first_name: '', last_name: '', firm_name: '', role: '' });
    setVCFormSuccess(false);
    setVCFormError('');
  };

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Header */}
      <header className="border-b border-slate-800 bg-slate-950/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center">
              <Rocket className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-bold text-white">The Founder Initiative</span>
          </div>
          <nav className="hidden md:flex gap-8 text-sm font-medium text-slate-400">
            <a href="/buyers" className="hover:text-white transition-colors">Buyers</a>
            <a href="/podcast" className="hover:text-white transition-colors">Podcast</a>
            <a href="/sponsors" className="hover:text-white transition-colors">Sponsors</a>
          </nav>
          <a 
            href="https://app.thefounderinitiative.com" 
            className="inline-flex items-center justify-center rounded-md text-sm font-medium bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white border-0 h-10 px-4 py-2"
          >
            Login
          </a>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-20 pb-32">
        <div className="absolute inset-0 bg-gradient-to-b from-blue-500/5 via-transparent to-transparent pointer-events-none"></div>
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-4xl mx-auto relative">
          <Badge className="mb-6 bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 border-blue-500/30">
            <Sparkles className="w-3 h-3 mr-1" />
            Live Pitch Events
          </Badge>
          <h1 className="text-6xl md:text-7xl font-bold text-white mb-6 leading-tight">
            Pitch Real Buyers
          </h1>
          <div className="h-20 flex items-center justify-center mb-8">
            <p className="text-2xl md:text-3xl font-semibold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent transition-all duration-500 ease-in-out">
              {rotatingBuyerTitles[currentTitleIndex]}
            </p>
          </div>
          <p className="text-xl text-slate-400 mb-12 leading-relaxed max-w-2xl mx-auto">
            Get real feedback, learn what enterprise buyers care about, and possibly land your next customer. These are buyers from previous events.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              onClick={() => setShowVideoModal(true)}
              className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white text-lg px-8 py-6 border-0"
              data-testid="watch-video-home-cta"
            >
              <Play className="mr-2 w-5 h-5" />
              See How It Works
            </Button>
            <a 
              href="https://calendly.com/adam-odonnell/meeting-with-adam-o-donnell-clone"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button 
                size="lg" 
                variant="outline"
                className="text-lg px-8 py-6 border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white w-full sm:w-auto"
              >
                Explore Sponsorships
              </Button>
            </a>
          </div>
        </div>
        </div>
      </section>

      {/* Hero Testimonial - Adam */}
      <section className="max-w-7xl mx-auto px-6 pb-24">
        {featuredTestimonial && (
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 via-cyan-500/20 to-purple-500/20 blur-3xl"></div>
            <Card className="relative border-slate-800 bg-slate-900/80 backdrop-blur-xl overflow-hidden">
              <CardContent className="p-12 md:p-16">
                <div className="flex flex-col md:flex-row items-center gap-12">
                  <div className="flex-shrink-0">
                    <div className="relative">
                      <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-cyan-500 blur-2xl opacity-30 rounded-full"></div>
                      <img 
                        src={featuredTestimonial.image_url || "https://customer-assets.emergentagent.com/job_pitch-buyers/artifacts/9zq9vvyy_1756586517100.jpeg"}
                        alt={featuredTestimonial.founder}
                        className="relative w-32 h-32 md:w-40 md:h-40 rounded-full object-cover ring-4 ring-blue-500/30"
                      />
                    </div>
                  </div>
                  <div className="flex-1 text-center md:text-left">
                    <div className="mb-6">
                      <svg className="w-12 h-12 text-blue-400 mb-4 mx-auto md:mx-0" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                      </svg>
                    </div>
                    <p className="text-2xl md:text-3xl font-bold text-white mb-6 leading-relaxed">
                      {featuredTestimonial.quote}
                    </p>
                    <div className="flex items-center gap-3 justify-center md:justify-start">
                      <div>
                        <p className="font-bold text-white text-lg">{featuredTestimonial.founder}</p>
                        <p className="text-blue-400 font-semibold">
                          {featuredTestimonial.title}
                          {featuredTestimonial.company && !featuredTestimonial.title?.toLowerCase().includes(featuredTestimonial.company?.toLowerCase()) && 
                            `, ${featuredTestimonial.company}`
                          }
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </section>

      {/* Featured Buyers Section */}
      <section id="buyers" className="max-w-7xl mx-auto px-6 pb-24">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-white mb-4">
            Decision Makers from Previous Events
          </h2>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            These senior buyers have participated in past pitch events, reviewing solutions and providing feedback to founders
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {decisionMakers.map((buyer) => (
            <Card key={buyer._id} className="hover:shadow-xl hover:shadow-blue-500/10 transition-all bg-slate-900/50 border-slate-800 backdrop-blur-sm">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  {buyer.image_url ? (
                    <img 
                      src={buyer.image_url} 
                      alt={buyer.name}
                      className="w-16 h-16 rounded-full bg-slate-800 ring-2 ring-slate-700 object-cover"
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center text-white font-bold text-xl ring-2 ring-slate-700">
                      {buyer.name.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div className="flex-1">
                    <h3 className="font-bold text-white text-lg mb-1">{buyer.name}</h3>
                    <p className="text-blue-400 font-semibold text-sm mb-1">{buyer.title}</p>
                    <p className="text-slate-300 font-medium mb-2">{buyer.company}</p>
                    <Badge variant="secondary" className="text-xs bg-slate-800 text-slate-300 border-slate-700">{buyer.category}</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
          
          {/* Join as Buyer Card */}
          <Card 
            data-testid="join-buyer-card"
            onClick={() => !showBuyerForm && setShowBuyerForm(true)}
            className={`transition-all ${
              showBuyerForm 
                ? 'bg-slate-900/80 border-blue-500/50' 
                : 'border-2 border-dashed border-slate-600 hover:border-blue-500/50 bg-transparent cursor-pointer hover:bg-slate-900/30'
            }`}
          >
            <CardContent className="p-6 h-full">
              {!showBuyerForm ? (
                /* Join CTA */
                <div className="flex flex-col items-center justify-center h-full min-h-[140px] text-center">
                  <div className="w-14 h-14 bg-blue-500/10 rounded-full flex items-center justify-center mb-4 ring-2 ring-dashed ring-blue-500/30">
                    <UserPlus className="w-7 h-7 text-blue-400" />
                  </div>
                  <h3 className="font-bold text-white text-lg mb-2">Want to be a Judge?</h3>
                  <p className="text-slate-400 text-sm">Join as a buyer to evaluate innovative startups</p>
                </div>
              ) : buyerFormSuccess ? (
                /* Success State */
                <div className="flex flex-col items-center justify-center h-full min-h-[140px] text-center">
                  <div className="w-14 h-14 bg-green-500/20 rounded-full flex items-center justify-center mb-4">
                    <CheckCircle2 className="w-7 h-7 text-green-400" />
                  </div>
                  <h3 className="font-bold text-white text-lg mb-2">Application Submitted!</h3>
                  <p className="text-slate-400 text-sm mb-4">We'll be in touch soon.</p>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={(e) => { e.stopPropagation(); resetBuyerForm(); }}
                    className="text-slate-400 hover:text-white"
                  >
                    Close
                  </Button>
                </div>
              ) : (
                /* Form */
                <div className="relative">
                  <button 
                    onClick={(e) => { e.stopPropagation(); resetBuyerForm(); }}
                    className="absolute -top-1 -right-1 p-1 text-slate-400 hover:text-white transition-colors"
                    data-testid="close-buyer-form"
                  >
                    <X className="w-4 h-4" />
                  </button>
                  
                  {buyerFormStep === 1 ? (
                    /* Step 1: Email */
                    <form onSubmit={handleBuyerEmailSubmit} className="space-y-4">
                      <div className="text-center mb-4">
                        <h3 className="font-bold text-white text-lg">Join as a Judge</h3>
                        <p className="text-slate-400 text-sm">Enter your work email</p>
                      </div>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <input
                          type="email"
                          data-testid="buyer-email-input"
                          placeholder="you@company.com"
                          value={buyerFormData.email}
                          onChange={(e) => setBuyerFormData({...buyerFormData, email: e.target.value})}
                          onClick={(e) => e.stopPropagation()}
                          className="w-full pl-10 pr-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        />
                      </div>
                      {buyerFormError && <p className="text-red-400 text-xs">{buyerFormError}</p>}
                      <Button 
                        type="submit" 
                        data-testid="buyer-email-submit"
                        className="w-full bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white"
                      >
                        Continue <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </form>
                  ) : (
                    /* Step 2: Details */
                    <form onSubmit={handleBuyerFormSubmit} className="space-y-3">
                      <div className="text-center mb-3">
                        <h3 className="font-bold text-white text-lg">Almost there!</h3>
                        <p className="text-slate-400 text-xs">{buyerFormData.email}</p>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="relative">
                          <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                          <input
                            type="text"
                            data-testid="buyer-firstname-input"
                            placeholder="First Name"
                            value={buyerFormData.first_name}
                            onChange={(e) => setBuyerFormData({...buyerFormData, first_name: e.target.value})}
                            onClick={(e) => e.stopPropagation()}
                            className="w-full pl-10 pr-3 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                          />
                        </div>
                        <div className="relative">
                          <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                          <input
                            type="text"
                            data-testid="buyer-lastname-input"
                            placeholder="Last Name"
                            value={buyerFormData.last_name}
                            onChange={(e) => setBuyerFormData({...buyerFormData, last_name: e.target.value})}
                            onClick={(e) => e.stopPropagation()}
                            className="w-full pl-10 pr-3 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-slate-400 text-xs mb-1.5 flex items-center gap-1">
                          <Briefcase className="w-3 h-3" /> What type of judge/buyer are you?
                        </label>
                        <div className="grid grid-cols-2 gap-1.5">
                          {buyerTypes.map((type) => (
                            <button
                              key={type}
                              type="button"
                              data-testid={`buyer-type-${type.toLowerCase().replace(' ', '-')}`}
                              onClick={(e) => { e.stopPropagation(); setBuyerFormData({...buyerFormData, buyer_type: type}); }}
                              className={`px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                                buyerFormData.buyer_type === type
                                  ? 'bg-blue-500 text-white'
                                  : 'bg-slate-800 text-slate-300 hover:bg-slate-700 border border-slate-700'
                              }`}
                            >
                              {type}
                            </button>
                          ))}
                        </div>
                      </div>
                      {buyerFormError && <p className="text-red-400 text-xs">{buyerFormError}</p>}
                      <div className="flex gap-2 pt-1">
                        <Button 
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); setBuyerFormStep(1); }}
                          className="text-slate-400 hover:text-white"
                        >
                          Back
                        </Button>
                        <Button 
                          type="submit" 
                          data-testid="buyer-form-submit"
                          disabled={buyerFormLoading}
                          className="flex-1 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white"
                        >
                          {buyerFormLoading ? 'Submitting...' : 'Submit Application'}
                        </Button>
                      </div>
                    </form>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="max-w-7xl mx-auto px-6 py-24">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-white mb-4">
            How It Works
          </h2>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            A simple, 60-minute event connecting founders with enterprise buyers
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Step 1 */}
          <Card className="border-slate-800 bg-slate-900/50 backdrop-blur-sm">
            <CardContent className="p-6">
              <div className="w-12 h-12 bg-blue-500/10 rounded-lg flex items-center justify-center mb-4 ring-1 ring-blue-500/20">
                <span className="text-2xl font-bold text-blue-400">1</span>
              </div>
              <h3 className="text-lg font-bold text-white mb-3">Apply to Pitch</h3>
              <p className="text-slate-400 text-sm leading-relaxed">
                Submit a 1-2 sentence solution statement about what you do. Anyone can attend, only founders can pitch.
              </p>
            </CardContent>
          </Card>

          {/* Step 2 */}
          <Card className="border-slate-800 bg-slate-900/50 backdrop-blur-sm">
            <CardContent className="p-6">
              <div className="w-12 h-12 bg-cyan-500/10 rounded-lg flex items-center justify-center mb-4 ring-1 ring-cyan-500/20">
                <span className="text-2xl font-bold text-cyan-400">2</span>
              </div>
              <h3 className="text-lg font-bold text-white mb-3">Get Selected</h3>
              <p className="text-slate-400 text-sm leading-relaxed mb-3">
                Two ways to pitch:
              </p>
              <ul className="text-slate-400 text-sm space-y-2">
                <li className="flex items-start gap-2">
                  <span className="text-blue-400 mt-0.5">•</span>
                  <span><strong className="text-slate-300">Pre-voted:</strong> Top 5 selected by senior buyers 1-2 days before</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-cyan-400 mt-0.5">•</span>
                  <span><strong className="text-slate-300">Live pick:</strong> Panel selects from remaining submissions during event</span>
                </li>
              </ul>
            </CardContent>
          </Card>

          {/* Step 3 */}
          <Card className="border-slate-800 bg-slate-900/50 backdrop-blur-sm">
            <CardContent className="p-6">
              <div className="w-12 h-12 bg-purple-500/10 rounded-lg flex items-center justify-center mb-4 ring-1 ring-purple-500/20">
                <span className="text-2xl font-bold text-purple-400">3</span>
              </div>
              <h3 className="text-lg font-bold text-white mb-3">Pitch & Review</h3>
              <p className="text-slate-400 text-sm leading-relaxed mb-3">
                <strong className="text-slate-300">90 seconds</strong> to pitch, then <strong className="text-slate-300">90 seconds</strong> of panel feedback:
              </p>
              <ul className="text-slate-400 text-xs space-y-1">
                <li>• What do they think you do?</li>
                <li>• Pain size (1-5 scale)</li>
                <li>• Budget availability</li>
                <li>• Who it impacts most</li>
              </ul>
            </CardContent>
          </Card>

          {/* Step 4 */}
          <Card className="border-slate-800 bg-slate-900/50 backdrop-blur-sm">
            <CardContent className="p-6">
              <div className="w-12 h-12 bg-green-500/10 rounded-lg flex items-center justify-center mb-4 ring-1 ring-green-500/20">
                <span className="text-2xl font-bold text-green-400">4</span>
              </div>
              <h3 className="text-lg font-bold text-white mb-3">Get Results</h3>
              <p className="text-slate-400 text-sm leading-relaxed mb-3">
                Panel gives one of four outcomes:
              </p>
              <div className="space-y-2 text-xs">
                <div className="flex items-start gap-2">
                  <Badge className="bg-slate-800 text-slate-300 border-slate-700 text-xs shrink-0">Not Now</Badge>
                  <span className="text-slate-400">Not interested in purchasing</span>
                </div>
                <div className="flex items-start gap-2">
                  <Badge className="bg-blue-500/10 text-blue-400 border-blue-500/30 text-xs shrink-0">Intro</Badge>
                  <span className="text-slate-400">Will intro you to the right person</span>
                </div>
                <div className="flex items-start gap-2">
                  <Badge className="bg-cyan-500/10 text-cyan-400 border-cyan-500/30 text-xs shrink-0">Demo</Badge>
                  <span className="text-slate-400">Wants to schedule a demo</span>
                </div>
                <div className="flex items-start gap-2">
                  <Badge className="bg-green-500/10 text-green-400 border-green-500/30 text-xs shrink-0">Advisor</Badge>
                  <span className="text-slate-400">Interested in advising your company</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Value Callout */}
        <div className="mt-12">
          <Card className="border-blue-500/30 bg-gradient-to-r from-blue-500/5 to-cyan-500/5 backdrop-blur-sm">
            <CardContent className="p-8 text-center">
              <h3 className="text-xl font-bold text-white mb-3">
                Value for Everyone
              </h3>
              <p className="text-slate-300 leading-relaxed max-w-3xl mx-auto">
                Not selected to pitch? You'll still gain valuable insights by listening to buyer feedback on similar solutions, participating in Q&A, and having your application shared with all panelists after the event.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* VC Partners & Sponsors */}
      <section id="partners" className="max-w-7xl mx-auto px-6 py-24">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-white mb-4">
            VC Referral Partners
          </h2>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            Here are a few leading venture firms that have referred startups to pitch at our events
          </p>
        </div>
        
        {/* VC Partners - Horizontal Scroll */}
        <div className="mb-12">
          <div className="relative overflow-hidden">
            <div className="flex gap-6 overflow-x-auto scrollbar-hide pb-4 snap-x snap-mandatory px-4">
              {vcPartners.map((vc) => (
                <div 
                  key={vc.id} 
                  className="flex-shrink-0 snap-center"
                >
                  <Card className="border-slate-800 bg-slate-900/50 hover:border-blue-500/50 transition-all backdrop-blur-sm w-56 h-40">
                    <CardContent className="p-8 flex flex-col items-center justify-center h-full">
                      {vc.logo_url ? (
                        <div className="w-full h-full flex items-center justify-center">
                          <img 
                            src={vc.logo_url} 
                            alt={vc.name}
                            className="max-w-full max-h-20 w-auto h-auto object-contain"
                            style={{ 
                              filter: 'brightness(0) invert(1)',
                              mixBlendMode: 'normal'
                            }}
                          />
                        </div>
                      ) : (
                        <div className="text-center">
                          <div className="w-12 h-12 bg-gradient-to-br from-blue-500/10 to-cyan-500/10 rounded-lg flex items-center justify-center mx-auto mb-3 ring-1 ring-blue-500/20">
                            <Building2 className="w-6 h-6 text-blue-400" />
                          </div>
                          <p className="text-sm font-bold text-white">{vc.name}</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Become a Partner CTA - Always Visible */}
        <div className="flex justify-center">
          <Card 
            data-testid="join-vc-card"
            onClick={() => !showVCForm && setShowVCForm(true)}
            className={`transition-all w-full max-w-md ${
              showVCForm 
                ? 'bg-slate-900/80 border-purple-500/50' 
                : 'border-2 border-dashed border-slate-600 hover:border-purple-500/50 bg-transparent cursor-pointer hover:bg-slate-900/30'
            }`}
          >
            <CardContent className="p-6">
              {!showVCForm ? (
                /* Join CTA */
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-purple-500/10 rounded-xl flex items-center justify-center ring-2 ring-dashed ring-purple-500/30 flex-shrink-0">
                    <UserPlus className="w-7 h-7 text-purple-400" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-white text-lg mb-1">Become a Referral Partner</h3>
                    <p className="text-slate-400 text-sm">Help your portfolio startups get exposure to enterprise buyers</p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-purple-400 flex-shrink-0" />
                </div>
              ) : vcFormSuccess ? (
                /* Success State */
                <div className="flex flex-col items-center justify-center py-4 text-center">
                  <div className="w-14 h-14 bg-green-500/20 rounded-full flex items-center justify-center mb-3">
                    <CheckCircle2 className="w-7 h-7 text-green-400" />
                  </div>
                  <h3 className="font-bold text-white text-xl mb-2">Application Submitted!</h3>
                  <p className="text-slate-400 mb-4">We'll be in touch soon.</p>
                  <Button 
                    variant="ghost" 
                    onClick={(e) => { e.stopPropagation(); resetVCForm(); }}
                    className="text-slate-400 hover:text-white"
                  >
                    Close
                  </Button>
                </div>
              ) : (
                /* Form */
                <div className="relative">
                  <button 
                    onClick={(e) => { e.stopPropagation(); resetVCForm(); }}
                    className="absolute top-0 right-0 p-1 text-slate-400 hover:text-white transition-colors"
                    data-testid="close-vc-form"
                  >
                    <X className="w-5 h-5" />
                  </button>
                  
                  {vcFormStep === 1 ? (
                    /* Step 1: Email */
                    <form onSubmit={handleVCEmailSubmit} className="space-y-4">
                      <div className="text-center mb-2">
                        <h3 className="font-bold text-white text-xl mb-1">Become a Referral Partner</h3>
                        <p className="text-slate-400 text-sm">Enter your work email to get started</p>
                      </div>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                        <input
                          type="email"
                          data-testid="vc-email-input"
                          placeholder="you@venturefirm.com"
                          value={vcFormData.email}
                          onChange={(e) => setVCFormData({...vcFormData, email: e.target.value})}
                          onClick={(e) => e.stopPropagation()}
                          className="w-full pl-11 pr-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        />
                      </div>
                      {vcFormError && <p className="text-red-400 text-sm">{vcFormError}</p>}
                      <Button 
                        type="submit" 
                        data-testid="vc-email-submit"
                        className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white py-3"
                      >
                        Continue <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </form>
                  ) : (
                    /* Step 2: Details */
                    <form onSubmit={handleVCFormSubmit} className="space-y-4">
                      <div className="text-center mb-2">
                        <h3 className="font-bold text-white text-xl mb-1">Almost there!</h3>
                        <p className="text-slate-400 text-sm">{vcFormData.email}</p>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="relative">
                          <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                          <input
                            type="text"
                            data-testid="vc-firstname-input"
                            placeholder="First Name"
                            value={vcFormData.first_name}
                            onChange={(e) => setVCFormData({...vcFormData, first_name: e.target.value})}
                            onClick={(e) => e.stopPropagation()}
                            className="w-full pl-10 pr-3 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                          />
                        </div>
                        <div className="relative">
                          <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                          <input
                            type="text"
                            data-testid="vc-lastname-input"
                            placeholder="Last Name"
                            value={vcFormData.last_name}
                            onChange={(e) => setVCFormData({...vcFormData, last_name: e.target.value})}
                            onClick={(e) => e.stopPropagation()}
                            className="w-full pl-10 pr-3 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                          />
                        </div>
                      </div>
                      <div className="relative">
                        <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <input
                          type="text"
                          data-testid="vc-firm-input"
                          placeholder="Firm Name"
                          value={vcFormData.firm_name}
                          onChange={(e) => setVCFormData({...vcFormData, firm_name: e.target.value})}
                          onClick={(e) => e.stopPropagation()}
                          className="w-full pl-10 pr-3 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-slate-400 text-sm mb-2">Your Role</label>
                        <div className="grid grid-cols-3 gap-2">
                          {vcRoles.map((role) => (
                            <button
                              key={role}
                              type="button"
                              data-testid={`vc-role-${role.toLowerCase()}`}
                              onClick={(e) => { e.stopPropagation(); setVCFormData({...vcFormData, role: role}); }}
                              className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                                vcFormData.role === role
                                  ? 'bg-purple-500 text-white'
                                  : 'bg-slate-800 text-slate-300 hover:bg-slate-700 border border-slate-700'
                              }`}
                            >
                              {role}
                            </button>
                          ))}
                        </div>
                      </div>
                      {vcFormError && <p className="text-red-400 text-sm">{vcFormError}</p>}
                      <div className="flex gap-3 pt-2">
                        <Button 
                          type="button"
                          variant="ghost"
                          onClick={(e) => { e.stopPropagation(); setVCFormStep(1); }}
                          className="text-slate-400 hover:text-white"
                        >
                          Back
                        </Button>
                        <Button 
                          type="submit" 
                          data-testid="vc-form-submit"
                          disabled={vcFormLoading}
                          className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white"
                        >
                          {vcFormLoading ? 'Submitting...' : 'Submit Application'}
                        </Button>
                      </div>
                    </form>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Testimonials */}
      <section className="bg-slate-900/30 text-white py-24 border-y border-slate-800">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4 text-white">What Founders Say</h2>
            <p className="text-slate-400">Real feedback from LinkedIn</p>
          </div>
          
          {/* Horizontal Scrolling Testimonials */}
          <div className="relative">
            <div 
              className={`flex gap-6 overflow-x-scroll pb-4 ${!isScrolling ? 'animate-scroll-left' : ''}`}
              style={{ 
                cursor: 'grab',
                scrollbarWidth: 'thin',
                scrollbarColor: '#475569 transparent'
              }}
              onScroll={handleScroll}
              onMouseDown={(e) => {
                e.currentTarget.style.cursor = 'grabbing';
              }}
              onMouseUp={(e) => {
                e.currentTarget.style.cursor = 'grab';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.cursor = 'grab';
              }}
            >
              {/* Duplicate testimonials for seamless loop */}
              {[...testimonials, ...testimonials].map((testimonial, index) => (
                <a 
                  key={`${testimonial.id}-${index}`}
                  href={testimonial.linkedin_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-shrink-0 block group w-96"
                  onClick={(e) => {
                    // Prevent navigation if user was scrolling/dragging
                    if (isScrolling) {
                      e.preventDefault();
                    }
                  }}
                >
                  <Card className="bg-slate-900/50 border-slate-800 backdrop-blur-sm hover:bg-slate-900/70 hover:border-blue-500/50 transition-all cursor-pointer h-full">
                    <CardContent className="p-6">
                      {/* LinkedIn Header */}
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-start gap-3">
                          <Avatar 
                            src={testimonial.image_url} 
                            name={testimonial.founder}
                            className="w-12 h-12"
                          />
                          <div>
                            <p className="font-bold text-white text-sm">{testimonial.founder}</p>
                            <p className="text-xs text-slate-400">{testimonial.title} at {testimonial.company}</p>
                            {testimonial.funding_info && (
                              <p className="text-xs text-blue-400 mt-1">{testimonial.funding_info}</p>
                            )}
                          </div>
                        </div>
                        {/* LinkedIn icon */}
                        <svg className="w-5 h-5 text-blue-500 group-hover:text-blue-400 transition-colors flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                        </svg>
                      </div>
                      
                      {/* Comment Text */}
                      <p className="text-slate-200 leading-relaxed text-sm">
                        "{testimonial.quote}"
                      </p>
                    </CardContent>
                  </Card>
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Startup Showcase */}
      <section id="startups" className="max-w-7xl mx-auto px-6 py-24">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-white mb-4">
            Featured Startups
          </h2>
          <p className="text-lg text-slate-400">
            Innovative companies that have pitched at our events
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {startups.map((startup) => (
            <Card key={startup.id} className="hover:shadow-xl hover:shadow-blue-500/10 transition-all border-slate-800 bg-slate-900/50 backdrop-blur-sm">
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-bold text-white text-xl">{startup.name}</h3>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-2">
                      <Badge variant="outline" className="text-xs border-slate-700 text-slate-400">{startup.category}</Badge>
                      <AchievementBadge achievement={startup.achievement} />
                    </div>
                  </div>
                  <Badge className="bg-blue-500/10 text-blue-400 border-blue-500/30 flex-shrink-0">{startup.stage}</Badge>
                </div>
                <p className="text-slate-400 mb-4 leading-relaxed">{startup.solution}</p>
                <div className="pt-4 border-t border-slate-800">
                  <p className="text-xs text-slate-500">Referred by <span className="font-semibold text-slate-300">{startup.referredBy}</span></p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Application CTA */}
      <section id="apply-section" className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 via-cyan-500/10 to-purple-500/10"></div>
        <div className="max-w-4xl mx-auto px-6 text-center relative">
          <h2 className="text-5xl font-bold mb-6 text-white">Apply to Pitch or Attend A Future Event</h2>
          <p className="text-xl text-slate-300 mb-12 leading-relaxed">
            Join startups from a16z, Y Combinator, Lightspeed, CRV, and beyond. Apply and we'll notify you when there's future events in your category.
          </p>
          <div className="bg-slate-900/80 backdrop-blur-xl rounded-2xl p-8 shadow-2xl border border-slate-800">
            <form className="space-y-6 text-left" onSubmit={async (e) => {
              e.preventDefault();
              const formData = new FormData(e.target);
              const data = {
                full_name: formData.get('full_name'),
                email: formData.get('email'),
                category: formData.get('category')
              };
              
              try {
                const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/applications`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                  },
                  body: JSON.stringify(data)
                });
                
                if (response.ok) {
                  alert('Application submitted successfully! We\'ll notify you about future events in your category.');
                  e.target.reset();
                } else {
                  alert('Error submitting application. Please try again.');
                }
              } catch (error) {
                console.error('Error:', error);
                alert('Error submitting application. Please try again.');
              }
            }}>
              <div>
                <label className="block text-sm font-semibold text-slate-300 mb-2">
                  Full Name *
                </label>
                <input 
                  type="text" 
                  required
                  name="full_name"
                  className="w-full px-4 py-3 border border-slate-700 bg-slate-800/50 rounded-lg text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Your name"
                />
                <p className="text-xs text-slate-400 mt-2 italic">Anyone can attend, but only founders can pitch.</p>
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-300 mb-2">
                  Email *
                </label>
                <input 
                  type="email" 
                  required
                  name="email"
                  className="w-full px-4 py-3 border border-slate-700 bg-slate-800/50 rounded-lg text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="founder@company.com"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-300 mb-2">
                  Buyer Categories *
                </label>
                <select 
                  required
                  name="category"
                  className="w-full px-4 py-3 border border-slate-700 bg-slate-800/50 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select a buyer category</option>
                  <option value="cybersecurity">Cybersecurity (pitch CISOs, etc)</option>
                  <option value="devtools">Developer Tools (pitch VPs of Eng., etc)</option>
                  <option value="fintech">Finance Tech (pitch VPs of Finance, etc)</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <Button 
                type="submit" 
                size="lg" 
                className="w-full bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white text-lg py-6 border-0"
              >
                Submit Application
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </form>
          </div>
        </div>
      </section>

      {/* Video Preview Modal */}
      {showVideoModal && (
        <div 
          className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowVideoModal(false);
          }}
        >
          <div className="relative w-full max-w-4xl">
            {/* Close Button */}
            <button
              onClick={() => setShowVideoModal(false)}
              className="absolute -top-12 right-0 text-white/80 hover:text-white p-2 transition-colors"
              data-testid="close-video-modal-home"
            >
              <X className="w-8 h-8" />
            </button>
            
            {/* Video Container */}
            <div className="bg-slate-900 rounded-xl overflow-hidden shadow-2xl">
              <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                <iframe
                  src={getEmbedUrl(previewVideoUrl)}
                  className="absolute inset-0 w-full h-full"
                  allow="autoplay; encrypted-media; fullscreen"
                  allowFullScreen
                  title="How It Works"
                />
              </div>
              
              {/* Modal Footer */}
              <div className="p-4 border-t border-slate-800">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-white font-semibold">See How It Works</h3>
                    <p className="text-slate-400 text-sm">Learn what to expect in a buyer pitch event</p>
                  </div>
                  <Button 
                    onClick={() => {
                      setShowVideoModal(false);
                      scrollToApplication();
                    }}
                    className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white border-0"
                  >
                    Apply to Pitch
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="bg-slate-950 text-slate-400 py-12 border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center gap-3 mb-6 md:mb-0">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center">
                <Rocket className="w-6 h-6 text-white" />
              </div>
              <span className="text-lg font-bold text-white">The Founder Initiative</span>
            </div>
            <p className="text-sm text-slate-500">
              © 2025 The Founder Initiative. Helping founders connect with real buyers.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
